import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

export function registerLogicalFunctions(registry: FunctionRegistry): void {
    registry.register('TRUE', () => true);

    registry.register('FALSE', () => false);

    registry.register('IF', (...args: FormulaValue[]) => {
        const condition = args[0];
        const trueVal = args[1] !== undefined ? args[1] : true;
        const falseVal = args[2] !== undefined ? args[2] : false;

        // Arrays are truthy; numbers follow standard JS truthiness; booleans directly
        let isTruthy: boolean;
        if (typeof condition === 'boolean') {
            isTruthy = condition;
        } else if (typeof condition === 'number') {
            isTruthy = condition !== 0;
        } else if (typeof condition === 'string') {
            const upper = condition.toUpperCase();
            if (upper === 'TRUE') isTruthy = true;
            else if (upper === 'FALSE') isTruthy = false;
            else isTruthy = condition !== '';
        } else if (Array.isArray(condition)) {
            isTruthy = condition.length > 0;
        } else {
            isTruthy = false;
        }

        return isTruthy ? trueVal : falseVal;
    });

    registry.register('IFS', (...args: FormulaValue[]) => {
        if (args.length < 2) throw new FormulaError(FormulaErrorType.VALUE, 'IFS requires at least 2 arguments');
        if (args.length % 2 !== 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'IFS requires an even number of arguments (condition/value pairs)');
        }
        for (let i = 0; i < args.length; i += 2) {
            const cond = args[i];
            let isTruthy: boolean;
            if (typeof cond === 'boolean') isTruthy = cond;
            else if (typeof cond === 'number') isTruthy = cond !== 0;
            else if (typeof cond === 'string') {
                const upper = cond.toUpperCase();
                if (upper === 'TRUE') isTruthy = true;
                else if (upper === 'FALSE') isTruthy = false;
                else isTruthy = cond !== '';
            } else if (Array.isArray(cond)) isTruthy = cond.length > 0;
            else isTruthy = false;

            if (isTruthy) return args[i + 1];
        }
        throw new FormulaError(FormulaErrorType.NA, 'IFS: no condition was true');
    });

    registry.register('AND', (...args: FormulaValue[]) => {
        if (args.length === 0) throw new FormulaError(FormulaErrorType.VALUE, 'AND requires at least 1 argument');
        const nums = flattenArgs(args);
        // Every value must be non-zero / truthy
        for (let i = 0; i < args.length; i++) {
            const v = args[i];
            if (Array.isArray(v)) {
                if (v.some(x => x === 0)) return false;
            } else if (typeof v === 'boolean') {
                if (!v) return false;
            } else if (typeof v === 'number') {
                if (v === 0) return false;
            } else if (typeof v === 'string') {
                const upper = v.toUpperCase();
                if (upper === 'FALSE' || upper === '0' || v === '') return false;
            } else if (v === null || v === undefined) {
                return false;
            }
        }
        return true;
    });

    registry.register('OR', (...args: FormulaValue[]) => {
        if (args.length === 0) throw new FormulaError(FormulaErrorType.VALUE, 'OR requires at least 1 argument');
        for (const v of args) {
            if (Array.isArray(v)) {
                if (v.some(x => x !== 0)) return true;
            } else if (typeof v === 'boolean') {
                if (v) return true;
            } else if (typeof v === 'number') {
                if (v !== 0) return true;
            } else if (typeof v === 'string') {
                const upper = v.toUpperCase();
                if (upper === 'TRUE') return true;
                const n = Number(v);
                if (!isNaN(n) && n !== 0) return true;
            }
        }
        return false;
    });

    registry.register('NOT', (...args: FormulaValue[]) => {
        const v = args[0];
        if (typeof v === 'boolean') return !v;
        if (typeof v === 'number') return v === 0;
        if (typeof v === 'string') {
            const upper = v.toUpperCase();
            if (upper === 'TRUE') return false;
            if (upper === 'FALSE') return true;
            const n = Number(v);
            return !isNaN(n) ? n === 0 : false;
        }
        if (v === null || v === undefined) return true;
        return false;
    });

    registry.register('XOR', (...args: FormulaValue[]) => {
        if (args.length === 0) throw new FormulaError(FormulaErrorType.VALUE, 'XOR requires at least 1 argument');
        let trueCount = 0;
        for (const v of args) {
            let isTruthy: boolean;
            if (typeof v === 'boolean') isTruthy = v;
            else if (typeof v === 'number') isTruthy = v !== 0;
            else if (Array.isArray(v)) isTruthy = v.some(x => x !== 0);
            else if (typeof v === 'string') {
                const upper = v.toUpperCase();
                if (upper === 'TRUE') isTruthy = true;
                else if (upper === 'FALSE') isTruthy = false;
                else {
                    const n = Number(v);
                    isTruthy = !isNaN(n) && n !== 0;
                }
            } else {
                isTruthy = false;
            }
            if (isTruthy) trueCount++;
        }
        return trueCount % 2 !== 0;
    });

    registry.register('IFERROR', (...args: FormulaValue[]) => {
        // Since errors are thrown before reaching here, the value arg is always
        // a valid FormulaValue if it did not throw. We check for FormulaError
        // instances passed as values (defensive) and otherwise return the value.
        const value = args[0];
        const errorValue = args[1] !== undefined ? args[1] : null;
        if (value instanceof FormulaError) return errorValue;
        return value !== undefined ? value : null;
    });

    registry.register('IFNA', (...args: FormulaValue[]) => {
        // Similar reasoning to IFERROR: check for NA FormulaError instances.
        const value = args[0];
        const naValue = args[1] !== undefined ? args[1] : null;
        if (value instanceof FormulaError && value.type === FormulaErrorType.NA) return naValue;
        return value !== undefined ? value : null;
    });

    registry.register('SWITCH', (...args: FormulaValue[]) => {
        if (args.length < 3) throw new FormulaError(FormulaErrorType.VALUE, 'SWITCH requires at least 3 arguments');
        const expr = args[0];
        // Pairs of (case, value), with optional trailing default
        const pairs = args.slice(1);
        const hasPairDefault = pairs.length % 2 !== 0;

        for (let i = 0; i < pairs.length - (hasPairDefault ? 1 : 0); i += 2) {
            const caseVal = pairs[i];
            const resultVal = pairs[i + 1];

            let matches: boolean;
            if (typeof expr === 'number' && typeof caseVal === 'number') {
                matches = expr === caseVal;
            } else if (typeof expr === 'string' && typeof caseVal === 'string') {
                matches = expr.toUpperCase() === caseVal.toUpperCase();
            } else if (typeof expr === 'boolean' && typeof caseVal === 'boolean') {
                matches = expr === caseVal;
            } else {
                matches = String(expr) === String(caseVal);
            }

            if (matches) return resultVal;
        }

        if (hasPairDefault) {
            return pairs[pairs.length - 1];
        }

        throw new FormulaError(FormulaErrorType.NA, 'SWITCH: no match found and no default provided');
    });
}
